/*
  mije-attribution — first-party click-ID capture and cart persistence.

  Reads the ad parameters off the landing URL, keeps a FIRST-touch record that
  is never overwritten and a LAST-touch record that is, and stamps both onto
  the Shopify cart as note attributes so every order carries the ad that
  earned it. analytics/attribution/ad_roi.py joins on those attributes.

  Rules this file lives by:
    - It must never throw into the page. Every storage read/write and every
      network call is wrapped; blocked storage degrades to "no attribution",
      never to an error.
    - A non-ad visit must never clobber an ad first touch; an ad visit may
      replace a non-ad first touch. Every first visit records a first touch
      (organic / direct / instagram-profile from the referrer) so a later
      tagged visit cannot inherit an empty slot - one write per visitor, then
      the signature shortcut keeps it quiet.
    - Underscore-prefixed attribute keys stay out of the customer's order
      confirmation while remaining readable through the Admin API.

  No dependencies. Deferred, so it never blocks render.
*/
(function () {
  'use strict';

  /* Bumped whenever the capture or write behaviour changes. Exposed on
     window.MijeAttribution so a deploy check can assert which build is
     actually executing — fetching the asset text is not reliable, because the
     CDN will happily serve a previous copy of the file to a different page for
     minutes after a push. */
  var VERSION = '2026-09-02-ft-organic';

  var BLOB = 'mije_attr_v1'; // compact record — the one thing the cookie carries
  var COOKIE = '_mije_attr';
  var DAYS = 365;
  var DEBOUNCE_MS = 5000;
  var MAX_BYTES = 4096;
  var LANDING_MAX = 500;
  var REFERRER_MAX = 300;

  var CLICK_IDS = ['fbclid', 'gclid', 'ttclid', 'msclkid', 'epik'];

  /* ------------------------------------------------------------- storage */
  /* Three layers, tried in order. Any of them may be unavailable (private
     mode, blocked cookies, storage quota) — that is expected, not an error. */

  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); return true; } catch (e) { return false; } }
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); return true; } catch (e) { return false; } }

  function cookieGet(name) {
    try {
      var m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[2]) : null;
    } catch (e) { return null; }
  }

  function cookieSet(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      // Only claim the apex domain when we are actually on it; on a
      // *.myshopify.com preview a .mije.shop cookie is silently rejected.
      var host = window.location.hostname || '';
      var domain = /(^|\.)mije\.shop$/i.test(host) ? '; domain=.mije.shop' : '';
      var secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + d.toUTCString() +
        '; path=/' + domain + '; SameSite=Lax' + secure;
      return true;
    } catch (e) { return false; }
  }

  function readState() {
    var raw = lsGet(BLOB) || cookieGet(COOKIE) || ssGet(BLOB);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function writeState(state) {
    var raw;
    try { raw = JSON.stringify(state); } catch (e) { return; }
    lsSet(BLOB, raw);
    cookieSet(COOKIE, raw, DAYS);
    ssSet(BLOB, raw);
    // Mirror the flat mije_ft_* / mije_lt_* keys into localStorage so the
    // record can be read at a glance in devtools. Never mirrored into the
    // cookie — the cookie stays small on purpose.
    try {
      ['ft', 'lt'].forEach(function (side) {
        var rec = state[side] || {};
        Object.keys(rec).forEach(function (f) {
          if (rec[f]) lsSet('mije_' + side + '_' + f, String(rec[f]));
        });
      });
      lsSet('mije_visit_count', String(state.visits || 0));
    } catch (e) { /* mirror is a convenience, never a requirement */ }
  }

  /* --------------------------------------------------------------- parse */

  function params() {
    var out = {};
    try {
      var qs = window.location.search || '';
      if (qs.charAt(0) === '?') qs = qs.slice(1);
      qs.split('&').forEach(function (pair) {
        if (!pair) return;
        var i = pair.indexOf('=');
        var k = i === -1 ? pair : pair.slice(0, i);
        var v = i === -1 ? '' : pair.slice(i + 1);
        try { out[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, ' ')); }
        catch (e) { out[k] = v; }
      });
    } catch (e) { /* unparseable query string — treat as organic */ }
    return out;
  }

  function truncate(s, n) {
    s = s == null ? '' : String(s);
    return s.length > n ? s.slice(0, n) : s;
  }

  /*
    Ad ids, with a fallback to the ones Meta appends itself.

    A real click URL from this account looks like:

      utm_source, utm_medium, utm_campaign, utm_content, utm_term,
      mije_ad_id, mije_adset_id, mije_campaign_id, mije_placement,
      mije_site_source, utm_id, fbclid, campaign_id, ad_id

    - 645 characters, of which fbclid alone is ~230. Meta appends its own
    bare `campaign_id` and `ad_id` at the very end, and their values match
    ours exactly (verified in live data).

    Long URLs get truncated somewhere between the ad platform and the browser,
    and a truncated URL loses its LAST parameters first - which means Meta's
    copies are the first casualties, not a reliable safety net. The real fix
    is the shortened parameter string in docs/ATTRIBUTION.md, which moves
    mije_ad_id from ~150 characters in to ~60. This fallback is the cheap
    belt-and-braces on top: it costs nothing and covers the case where our own
    tag is missing or cut but the URL survived far enough to keep Meta's.
  */
  function resolveId(p, primary, fallback) {
    var ours = (p[primary] || '').trim();
    var theirs = (p[fallback] || '').trim();
    if (!ours) return theirs;
    if (!theirs || ours === theirs) return ours;
    // A truncated id is a PREFIX of the full one ("12025448" vs
    // "120254489046890322"). When both survived and one is a prefix of the
    // other, the longer is the intact one.
    if (theirs.indexOf(ours) === 0) return theirs;
    if (ours.indexOf(theirs) === 0) return ours;
    // Genuinely different values: keep ours. We control that parameter, and a
    // disagreement is more likely a stale platform param than a wrong tag.
    return ours;
  }

  /*
    Which ad network a visit came from, resolved in a fixed order so the same
    URL always produces the same answer:

      1. `mije_network` — declared by the ad's own URL parameters. Explicit
         always beats inferred, and it is what the warehouse prefers too.
      2. `utm_source` — also deliberately tagged, and it is the signal that
         survives when our own `mije_*` parameters were cut off the end of a
         truncated URL but the platform's click id was appended after them.
      3. the captured click ID — the weakest signal, because a click id can
         outlive the click it belonged to in a URL that is copied, shared or
         bookmarked.

    An organic visit resolves to '' and still writes no network attribute, so
    this cannot invent attribution where there was none.

    PRECEDENCE when a URL carries more than one click id (say both `fbclid` and
    `ttclid`): steps 1 and 2 settle it on the declared network, which is the
    case that actually occurs on tagged ads. Only a URL with no `mije_network`
    AND no recognised `utm_source` reaches step 3, where CLICK_IDS order decides
    — `fbclid` first. That order is deliberately left untouched: it is what
    guarantees a Meta click's captured click_id and network cannot change.
  */
  var SOURCE_NETWORKS = {
    facebook: 'meta', fb: 'meta', meta: 'meta', instagram: 'meta', ig: 'meta',
    tiktok: 'tiktok', tt: 'tiktok', tiktok_ads: 'tiktok',
    google: 'google', adwords: 'google', googleads: 'google',
    bing: 'bing', microsoft: 'bing',
    pinterest: 'pinterest', pin: 'pinterest'
  };

  /* Keyed by click-ID parameter name — the same names, in the same order, as
     CLICK_IDS above. An unrecognised utm_source (a newsletter, an affiliate)
     deliberately falls through to '' rather than becoming its own network:
     the network dimension is for ad platforms, not for every traffic source. */
  var CLICK_ID_NETWORKS = {
    fbclid: 'meta', gclid: 'google', ttclid: 'tiktok',
    msclkid: 'bing', epik: 'pinterest'
  };

  function resolveNetwork(p, clickType) {
    var declared = (p.mije_network || '').trim().toLowerCase();
    if (declared) return declared;
    var src = (p.utm_source || '').trim().toLowerCase();
    if (src && SOURCE_NETWORKS[src]) return SOURCE_NETWORKS[src];
    if (clickType && CLICK_ID_NETWORKS[clickType]) return CLICK_ID_NETWORKS[clickType];
    return '';
  }

  /* ─────────────────────────────── ad vs tagged vs organic (2026-09-02) ──
     Three questions the old single isPaid() collapsed into one, and the
     collapse is what cost first touches: an Instagram link-in-bio tap
     (utm_source=ig&utm_medium=social + an fbclid) counted as "paid", claimed
     the first-touch slot, and a real ad click that came BEFORE or AFTER it
     was demoted to last touch or lost (ATTR-FORENSICS-0901: 6 of 6 leaked
     orders, all this shape).

       isAd(p)      the visit is a bought click: our own mije_* ids, the
                    platform's appended ad_id/campaign_id, or a click id that
                    arrives WITH a paid medium. A bare fbclid is not an ad.
       isTagged(p)  anything deliberately tagged - any utm_*, any click id,
                    any ad id. Updates LAST touch; never on its own the first.
       organic      nothing at all - the first touch is still recorded, from
                    the referrer, so a later email or bio visit can no longer
                    inherit an empty slot.

     THE FT SLOT RULE: an ad touch may replace a non-ad first touch; a non-ad
     touch may never replace an ad first touch. Between two ads, or between
     two non-ads, the older record wins. */
  var PAID_MEDIUMS = { paid: 1, cpc: 1, ppc: 1, paidsocial: 1, paid_social: 1,
                       display: 1, ads: 1, cpm: 1 };
  var SEARCH_HOSTS = /(^|\.)(google|bing|yahoo|duckduckgo|ecosia|baidu|naver|yandex)\./i;
  var IG_HOSTS = /(^|\.)(l\.)?instagram\.com$/i;
  var OWN_HOSTS = /(^|\.)(mije\.shop|myshopify\.com|shopify\.com)$/i;

  function hasClickId(p) {
    for (var i = 0; i < CLICK_IDS.length; i++) if (p[CLICK_IDS[i]]) return true;
    return false;
  }
  function paidMedium(p) {
    return !!PAID_MEDIUMS[String(p.utm_medium || '').trim().toLowerCase()];
  }
  function isAd(p) {
    if (p.mije_ad_id || p.mije_adset_id || p.mije_campaign_id) return true;
    // Meta appends these itself; a click that kept only those is still paid.
    if (p.ad_id || p.campaign_id) return true;
    // A click id proves a platform tap, not a bought one (Instagram stamps an
    // fbclid on every outbound tap). With a paid medium beside it, it is an ad.
    if (hasClickId(p) && (paidMedium(p) || p.mije_network)) return true;
    return false;
  }
  function isTagged(p) {
    if (isAd(p)) return true;
    if (p.utm_source || p.utm_medium || p.utm_campaign || p.utm_content || p.utm_term) return true;
    return hasClickId(p);
  }
  /* Same test on a STORED touch (state or cart record), where only the
     resolved fields survive: ids, or a click id that came with a paid medium. */
  function isAdTouch(t) {
    if (!t) return false;
    if (t.ad_id || t.adset_id || t.campaign_id) return true;
    return !!(t.click_id && PAID_MEDIUMS[String(t.medium || '').trim().toLowerCase()]);
  }
  function refHost() {
    try { return document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) { return ''; }
  }
  /* The first touch of an UNTAGGED visit. Source/medium come from the
     referrer - the only evidence there is - and no id of any kind. The
     warehouse's organic-medium veto reads 'search', 'profile', 'none' and
     'referral' as organic (mije-data resolve_network, rule 2). */
  function organicTouch() {
    var host = refHost();
    var source = 'direct', medium = 'none';
    if (host && !OWN_HOSTS.test(host)) {
      if (SEARCH_HOSTS.test(host)) { source = 'organic'; medium = 'search'; }
      else if (IG_HOSTS.test(host)) { source = 'instagram'; medium = 'profile'; }
      else { source = 'referral'; medium = 'referral'; }
    }
    var landing = '';
    try { landing = truncate(window.location.pathname + window.location.search, LANDING_MAX); } catch (e) {}
    return {
      source: source, medium: medium, campaign: '', content: '', term: '',
      ad_id: '', adset_id: '', campaign_id: '', network: '', placement: '',
      site_source: '', click_id: '', click_type: '', landing: landing,
      referrer: truncate(document.referrer || '', REFERRER_MAX),
      ts: new Date().toISOString()
    };
  }

  function touchFrom(p) {
    var clickId = '';
    var clickType = '';
    for (var i = 0; i < CLICK_IDS.length; i++) {
      if (p[CLICK_IDS[i]]) { clickId = p[CLICK_IDS[i]]; clickType = CLICK_IDS[i]; break; }
    }
    var landing = '';
    try { landing = truncate(window.location.pathname + window.location.search, LANDING_MAX); } catch (e) {}
    /* A visit tagged only with mije_* ids (no utm_source, no click ID) is
       still a paid visit — it just came from a link where only our own
       parameters were set. Give it an explicit source so the field is never
       empty and such visits are distinguishable in reporting rather than
       looking like organic traffic with an ad id attached. */
    var adId = resolveId(p, 'mije_ad_id', 'ad_id');
    var campaignId = resolveId(p, 'mije_campaign_id', 'campaign_id');

    var source = p.utm_source || '';
    var medium = p.utm_medium || '';
    if (!source && (adId || p.mije_adset_id || campaignId)) {
      source = 'direct-tagged';
    }
    /* A BARE fbclid - no utm, no mije_*, no platform id - is an Instagram
       profile tap (bio link, DM, organic post), not a bought click. Name it
       so, keep the click id as evidence, and declare NO network: the
       warehouse must not read it as Meta. */
    var bareFbclid = clickType === 'fbclid' && !source && !medium && !adId &&
                     !p.mije_adset_id && !campaignId && !p.mije_network;
    if (bareFbclid) { source = 'instagram'; medium = 'profile'; }
    return {
      source: source,
      medium: medium,
      campaign: p.utm_campaign || '',
      content: p.utm_content || '',
      term: p.utm_term || '',
      ad_id: adId,
      adset_id: p.mije_adset_id || '',
      campaign_id: campaignId,
      /* Declared by the shortened parameter string when it is there, inferred
         from utm_source or the click id when it is not. The warehouse's
         resolve_network() checks the declared value FIRST — explicit beats
         inferred — so a tagged link no longer depends on utm_source surviving
         truncation, and an untagged one is no longer network-less. */
      network: bareFbclid ? '' : resolveNetwork(p, clickType),
      placement: p.mije_placement || '',
      site_source: p.mije_site_source || '',
      click_id: clickId,
      click_type: clickType,
      landing: landing,
      referrer: truncate(document.referrer || '', REFERRER_MAX),
      ts: new Date().toISOString()
    };
  }

  /* A visit "carries attribution" when it has a click ID, a utm_source, or
     any of our own ad ids. That last case matters: a link tagged with only
     mije_ad_id (an influencer link, a QR code, a hand-built URL) is every bit
     as attributable as one with a full UTM set, and previously fell through
     to organic and was thrown away. Anything less than this is organic and
     must not touch the last-touch record. */
  /* isPaid() is gone. It answered "does this URL carry anything" and was used
     as "is this a bought click", which are different questions - see the ad /
     tagged / organic block above. */

  function isEmptyTouch(t) {
    if (!t) return true;
    return !(t.source || t.click_id || t.ad_id || t.campaign_id || t.adset_id);
  }

  /* ---------------------------------------------------------- build state */

  var p = params();
  var ad = isAd(p);
  var tagged = isTagged(p);
  var paid = tagged;   // the name the cart-reconcile path reads: "this visit was tagged"
  var state = readState() || { ft: null, lt: null, visits: 0 };

  // One visit per session, so page views inside a session don't inflate it.
  if (!ssGet('mije_visit_seen')) {
    state.visits = (Number(state.visits) || 0) + 1;
    ssSet('mije_visit_seen', '1');
  }

  /* FIRST touch is written on EVERY first visit, tagged or not. An ad visit
     may also REPLACE a non-ad first touch (the bio tap that held the slot
     until the real click arrived); nothing ever replaces an ad first touch.
     LAST touch moves only on a tagged visit - an organic page view must never
     overwrite a real last touch. */
  var touch = tagged ? touchFrom(p) : organicTouch();
  if (isEmptyTouch(state.ft) || (ad && !isAdTouch(state.ft))) state.ft = touch;
  if (tagged) state.lt = touch;

  var hasAny = !isEmptyTouch(state.ft) || !isEmptyTouch(state.lt);
  if (hasAny || state.visits) writeState(state);

  /* --------------------------------------------------- cart attributes */

  /* Ordered by how much we would hate to lose it. The ad ids identify the
     creative that earned the order and are never dropped by the size cap;
     landing page and referrer are the first things to go. */
  function buildAttributes() {
    var ft = state.ft || {};
    var lt = state.lt || {};
    // A visitor with no attribution at all gets NOTHING — not even a visit
    // count. Any key here would trigger a cart write for organic traffic.
    if (isEmptyTouch(state.ft) && isEmptyTouch(state.lt)) return {};
    var tiers = [
      // tier 0 — never dropped
      [
        ['_mije_ft_ad_id', ft.ad_id], ['_mije_ft_adset_id', ft.adset_id],
        ['_mije_ft_campaign_id', ft.campaign_id], ['_mije_ft_click_id', ft.click_id],
        // Declared network. The warehouse checks this before inferring from
        // click-id shape or utm_source, so attribution no longer depends on
        // utm_source surviving a truncated URL. Tier 0: never dropped.
        ['_mije_network', ft.network],
        // Placement rides in tier 0 with the ids, not with the reporting
        // dimensions: it is a primary cut, and a placement dropped by the
        // size cap is indistinguishable from a placement that was never
        // tagged. Costs ~35 bytes against a 4096 cap.
        ['_mije_ft_placement', ft.placement],
        ['_mije_lt_ad_id', lt.ad_id], ['_mije_lt_click_id', lt.click_id]
      ],
      // tier 1 — the reporting dimensions
      [
        ['_mije_ft_source', ft.source], ['_mije_ft_medium', ft.medium],
        ['_mije_ft_campaign', ft.campaign], ['_mije_ft_content', ft.content],
        ['_mije_ft_ts', ft.ts],
        ['_mije_lt_source', lt.source], ['_mije_lt_medium', lt.medium],
        ['_mije_lt_campaign', lt.campaign], ['_mije_lt_ts', lt.ts],
        ['_mije_visits', state.visits ? String(state.visits) : '']
      ],
      // tier 2 — context, dropped first
      [
        ['_mije_ft_landing', ft.landing], ['_mije_ft_referrer', ft.referrer]
      ]
    ];

    var attrs = {};
    var order = [];
    tiers.forEach(function (tier, ti) {
      tier.forEach(function (kv) {
        if (kv[1]) { attrs[kv[0]] = String(kv[1]); order.push([kv[0], ti]); }
      });
    });

    function size() {
      try { return JSON.stringify({ attributes: attrs }).length; } catch (e) { return 0; }
    }
    // Shrink lowest tier first: truncate, then drop outright.
    for (var t = 2; t >= 1 && size() > MAX_BYTES; t--) {
      order.forEach(function (o) {
        if (o[1] !== t || size() <= MAX_BYTES) return;
        if (attrs[o[0]] && attrs[o[0]].length > 60) attrs[o[0]] = attrs[o[0]].slice(0, 60);
      });
      order.forEach(function (o) {
        if (o[1] !== t || size() <= MAX_BYTES) return;
        delete attrs[o[0]];
      });
    }
    return attrs;
  }

  /* -------------------------------------------------------- cart writing */

  var lastWriteAt = 0;
  var pending = null;

  function payloadOf(attrs) {
    try { return JSON.stringify(attrs); } catch (e) { return ''; }
  }

  function alreadyWrote(sig) {
    return ssGet('mije_attr_written') === sig;
  }

  function markWritten(sig) {
    ssSet('mije_attr_written', sig);
  }

  /*
    force=true is used right after an add-to-cart, because that is the moment
    a cart can be BRAND NEW (or freshly cleared) and therefore carry none of
    our attributes — the "unchanged" shortcut would wrongly skip it there.
    Every other trigger takes the shortcut and stays quiet.
  */
  function writeCart(force, keepalive) {
    var attrs = buildAttributes();
    var keys = Object.keys(attrs);
    if (!keys.length) return;                       // organic-only: never write
    var sig = payloadOf(attrs);
    if (!force && alreadyWrote(sig)) return;        // unchanged: never write

    /*
      A FORCED WRITE IS NEVER DEBOUNCED.

      It used to be, and that quietly ate the one write that matters most. The
      checkout click calls writeCart(true, true) — forced, keepalive, so it can
      outlive the navigation. But if any write had gone out in the previous
      5 seconds this fell into the branch below, scheduled a setTimeout, and
      returned; the navigation to /checkout then destroyed the timer and the
      attributes were never sent. Worse, `if (pending) return` dropped the
      forced write outright whenever an earlier one was already queued.

      Debouncing exists to stop idle chatter, not to delay a write we have
      explicitly decided we need. force now goes straight out.
    */
    if (force) { send(attrs, sig, keepalive); return; }

    var now = Date.now();
    var wait = Math.max(0, DEBOUNCE_MS - (now - lastWriteAt));
    if (wait > 0) {
      if (pending) return;
      pending = window.setTimeout(function () { pending = null; send(attrs, sig, keepalive); }, wait);
      return;
    }
    send(attrs, sig, keepalive);
  }

  function send(attrs, sig, keepalive) {
    lastWriteAt = Date.now();
    try {
      var opts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      };
      // keepalive lets the write survive the navigation to /checkout
      if (keepalive) opts.keepalive = true;
      var f = (window.__mijeOrigFetch || window.fetch);
      f('/cart/update.js', opts).then(function (res) {
        if (res && res.ok) {
          markWritten(sig);
          // The response body is the whole cart — free, current pricing for
          // the funnel events. Cloning keeps the caller's stream untouched.
          try { res.clone().json().then(noteCart).catch(function () {}); } catch (e) {}
        }
      }).catch(function () { /* offline / throttled — next trigger retries */ });
    } catch (e) { /* never surface into the page */ }
  }

  /* ------------------------------------------------------------ triggers */

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // 1. the theme's own add-to-cart event
  document.addEventListener('mije:cart:added', function (ev) {
    writeCart(true);
    try { captureAddToCart((ev && ev.detail) || null); } catch (e) {}
  });

  // 2. fallback: intercept /cart/add.js ourselves, in case the theme event
  //    ever moves or another surface adds without dispatching it. This wraps
  //    fetch BEFORE the cart drawer does (we are first in <head>), so the
  //    drawer's own wrapper still sees every call.
  try {
    var origFetch = window.fetch;
    window.__mijeOrigFetch = origFetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var out = origFetch.apply(this, arguments);
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        try {
          out = out.then(function (res) {
            if (res && res.ok) {
              writeCart(true);
              // /cart/add.js echoes the added line; clone so the theme's own
              // handler still gets an unread body.
              try { res.clone().json().then(captureAddToCart).catch(function () { captureAddToCart(null); }); }
              catch (e) { captureAddToCart(null); }
            }
            return res;
          });
        } catch (e) { /* leave the original promise untouched */ }
      }
      return out;
    };
  } catch (e) { /* no fetch to wrap — the theme event still covers us */ }

  try {
    var origSend = window.XMLHttpRequest && window.XMLHttpRequest.prototype.send;
    var origOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
    if (origOpen && origSend) {
      window.XMLHttpRequest.prototype.open = function (m, u) {
        try { this.__mijeUrl = u; } catch (e) {}
        return origOpen.apply(this, arguments);
      };
      window.XMLHttpRequest.prototype.send = function () {
        try {
          var self = this;
          if (/\/cart\/add(\.js)?(\?|$)/.test(self.__mijeUrl || '')) {
            self.addEventListener('load', function () {
              if (self.status < 200 || self.status >= 300) return;
              writeCart(true);
              var line = null;
              try { line = JSON.parse(self.responseText); } catch (e) {}
              try { captureAddToCart(line); } catch (e) {}
            });
          }
        } catch (e) {}
        return origSend.apply(this, arguments);
      };
    }
  } catch (e) {}

  onReady(function () {
    // 3. cart drawer open — watch the panel's class rather than coupling to
    //    the drawer's internals
    try {
      var panel = document.querySelector('[data-cart-panel], .mj-cart');
      if (panel && window.MutationObserver) {
        var wasOpen = panel.classList.contains('is-open');
        new window.MutationObserver(function () {
          var open = panel.classList.contains('is-open');
          if (open && !wasOpen) writeCart(false);
          wasOpen = open;
        }).observe(panel, { attributes: true, attributeFilter: ['class'] });
      }
    } catch (e) {}

    // 4. /cart page load
    try {
      if (/^\/cart(\/|$|\?)/.test(window.location.pathname)) writeCart(false);
    } catch (e) {}

    /*
      5. navigation to checkout — keepalive so it lands before the page goes.

      POINTERDOWN AS WELL AS CLICK. click fires after the button's own handler
      has already begun the navigation, and on a form submit the browser can
      tear the document down before our request leaves. pointerdown fires on
      the press, tens of milliseconds earlier, which on a slow phone is the
      difference between the write leaving and not. Both are bound because a
      keyboard-activated button never produces a pointer event; the signature
      shortcut collapses the duplicate.
    */
    try {
      var CHECKOUT_SEL = 'a[href*="/checkout"], [name="checkout"], [data-checkout], ' +
                         'button[type="submit"][form*="cart"], .mj-cart__checkout';
      var checkoutWrite = function (e) {
        var a = e.target && e.target.closest && e.target.closest(CHECKOUT_SEL);
        if (a) {
          writeCart(true, true);
          try { captureCheckoutStarted(); } catch (err) {}
        }
      };
      document.addEventListener('pointerdown', checkoutWrite, true);
      document.addEventListener('click', checkoutWrite, true);
      document.addEventListener('submit', function (e) {
        var f = e.target;
        try {
          if (f && (/\/checkout/.test(f.action || '') || f.querySelector('[name="checkout"]'))) {
            writeCart(true, true);
            try { captureCheckoutStarted(); } catch (err) {}
          }
        } catch (err) {}
      }, true);
    } catch (e) {}

    /*
      6. THE CART IS THE DURABLE STORE — read it, then stamp it.

      WHAT THIS REPLACED, AND WHY. The old version fetched /cart.js and wrote
      only `if (cart.item_count > 0)`. That left one window open, and it is the
      window every lost order fell through:

        page 1  ad click lands on a PDP. We have the full record — but the cart
                is EMPTY, so item_count is 0 and nothing is written.
        page 2  no ad parameters in the URL, so state is rebuilt from storage.
                In an in-app webview (every lost order arrived with
                ft_source=ig) storage is frequently unavailable, so readState()
                returns null and state.ft is empty.
        page 3  add to cart fires writeCart(true) — which computes {} and
                returns. The order ships with no attribution at all.

      Reproduced on the live theme with storage denied: 17 attributes on the
      landing page, 0 on the next, none on the cart. See _repro_blocked.py.

      The fix is to stop treating browser storage as the only memory. Shopify
      keeps note attributes on an EMPTY cart and carries them when lines are
      added later, so the cart is a server-side record that survives everything
      the browser can deny us. Two changes:

        READ  — if storage gave us nothing but the cart already carries a first
                touch, adopt it. That repairs the visitor whose record was lost
                between pages, and it is what makes the ft_* super properties
                keep flowing into PostHog after page 1.

        WRITE — stamp as soon as we have something to stamp, empty cart or not.

      Cost: one GET per page, unchanged, plus one POST on a tagged landing.
      The signature shortcut still suppresses every repeat write after that.
    */
    function touchFromCartAttrs(a) {
      if (!a) return null;
      var t = {
        source: a._mije_ft_source || '', medium: a._mije_ft_medium || '',
        campaign: a._mije_ft_campaign || '', content: a._mije_ft_content || '',
        term: '', ad_id: a._mije_ft_ad_id || '', adset_id: a._mije_ft_adset_id || '',
        campaign_id: a._mije_ft_campaign_id || '', network: a._mije_network || '',
        placement: a._mije_ft_placement || '', site_source: '',
        click_id: a._mije_ft_click_id || '', click_type: '',
        landing: a._mije_ft_landing || '', referrer: a._mije_ft_referrer || '',
        ts: a._mije_ft_ts || ''
      };
      return isEmptyTouch(t) ? null : t;
    }

    /*
      THE CART'S FIRST TOUCH ALWAYS WINS.

      "Never clobber an existing ft_*" is the whole contract of a first-touch
      model, and the cart's copy is by definition the older one — it was
      written on an earlier visit, possibly from a device state we can no
      longer see. So when both exist we keep the cart's as ft and let this
      visit be the LAST touch, which is exactly the rule the in-memory record
      already follows. A visitor who clicks a second ad therefore still reads
      ft = the ad that opened them, lt = the ad that brought them back.
    */
    /*
      AN AD FIRST TOUCH ALWAYS WINS (2026-09-02; was "the cart's first touch
      always wins").

      The cart's record is the older one, and older wins BETWEEN EQUALS: two
      ad touches, or two non-ad touches. But when the cart holds a bio /
      organic first touch and this browser holds an ad click, the ad is the
      first touch - the cart's copy was only there because a tap with no
      money behind it got in first (ATTR-FORENSICS-0901: 3 of 6 leaked orders
      had the real ad click demoted to _mije_lt_ad_id by the old rule).
    */
    function reconcileWithCart(cart) {
      var fromCart = touchFromCartAttrs(cart && cart.attributes);
      if (!fromCart) return;
      var localIsAd = isAdTouch(state.ft);
      var cartIsAd = isAdTouch(fromCart);
      var same = !isEmptyTouch(state.ft) &&
                 String(state.ft.ad_id || '') === String(fromCart.ad_id || '') &&
                 String(state.ft.source || '') === String(fromCart.source || '') &&
                 String(state.ft.ts || '') === String(fromCart.ts || '');
      if (same) return;
      if (localIsAd && !cartIsAd) {
        // Local ad ft beats the cart's non-ad ft: keep ours, push it now.
        if (isEmptyTouch(state.lt)) state.lt = state.ft;
        writeState(state);
        try { reregisterSuperProps(); } catch (e) {}
        writeCart(true);
        return;
      }
      // Cart wins: it is an ad ft over our non-ad ft, or the older of two
      // equals. This visit becomes last touch only if it was itself tagged.
      if (paid && !isEmptyTouch(state.ft)) state.lt = state.ft;
      state.ft = fromCart;
      if (isEmptyTouch(state.lt)) state.lt = fromCart;
      writeState(state);
      try { reregisterSuperProps(); } catch (e) {}
    }

    /* The cart carries our mark when it holds either an ad id or a first-touch
       source - an organic first touch is a mark too, now that it is written. */
    function cartHasMark(a) {
      return !!(a && (a._mije_ft_ad_id || a._mije_ft_source));
    }

    function bootstrapFromCart() {
      /* AD LANDING: stamp BEFORE the reconcile read. In the Instagram in-app
         webview the read can return a cart that a bio tap stamped seconds
         earlier; writing first means the ad click is on the cart whatever the
         read says, and the reconcile then keeps it (ad ft always wins). */
      if (ad) { try { writeCart(true, true); } catch (e) {} }
      var f0 = window.__mijeOrigFetch || window.fetch;
      return f0('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cart) {
          var a = (cart && cart.attributes) || {};
          if (cart) {
            noteCart(cart);
            reconcileWithCart(cart);
          }
          /*
            THE "UNCHANGED PAYLOAD" SHORTCUT CANNOT BE TRUSTED ACROSS A CART SWAP.

            alreadyWrote() compares the payload we are about to send against the
            last one that succeeded, and skips the write when they match. That
            assumes the cart we wrote to is still the cart we have. A cart
            permalink (/cart/<variant>:<qty>) REPLACES the cart server-side, so
            the payload is identical while the new cart carries nothing — and
            the shortcut then suppresses the one write that would have fixed it.
            Measured: 16 attributes before the permalink, 0 after, even with
            storage working perfectly.

            So the decision is made on what the CART actually holds, not on what
            we last sent. Missing our mark means force. This also covers a cart
            cleared by an app or rebuilt by a subscription tool.

            It stays idempotent: once the mark is there this takes the quiet
            path, and an organic visitor is still stopped by the empty-payload
            guard inside writeCart().
          */
          writeCart(!cartHasMark(a));
        })
        .catch(function () { /* throttled or offline — other triggers cover it */ });
    }

    try { bootstrapFromCart(); } catch (e) {}

    /*
      6b. CART-LINK LANDINGS.

      A cart link builds a cart server-side before any of our triggers can hear
      about it — no /cart/add.js is made, so the fetch and XHR wrappers see
      nothing at all.

      MEASURED, BECAUSE THE TWO FORMS BEHAVE DIFFERENTLY:

        /cart/<variant>:<qty>   does NOT land on the storefront. Shopify
                                redirects it straight to /checkouts/cn/…, where
                                theme JS does not run — window.MijeAttribution
                                does not even exist there. Nothing in this file
                                can stamp that cart, and no change to it will;
                                the limit is Shopify's, not ours. Recorded in
                                docs/ATTRIBUTION.md rather than papered over.

        ?cart_link_id=…         DOES land on /cart, which is the shape order
                                #1658 arrived through. That page is ours, and
                                bootstrapFromCart() above stamps it because the
                                new cart is missing our mark.

      This re-assert covers the timing race on the second form: the redirect can
      deliver the page while Shopify is still building the cart, so the first
      read comes back empty and the bootstrap write is skipped.
    */
    try {
      if (/[?&]cart_link_id=/.test(window.location.search)) {
        window.setTimeout(function () { writeCart(true); }, 1500);
      }
    } catch (e) {}

    /*
      6c. PERIODIC RE-ASSERT.

      A tab left open across a cart the shopper edits from another surface, a
      cart rebuilt by an app, a checkout opened much later — all of them can
      end up with lines but no stamp. The signature shortcut makes this free
      when nothing has changed: it is a GET and, on an unchanged cart, no POST.
    */
    try {
      var REASSERT_MS = 45000;
      window.setInterval(function () {
        try {
          if (!Object.keys(buildAttributes()).length) return;
          var f1 = window.__mijeOrigFetch || window.fetch;
          f1('/cart.js', { cache: 'no-store' })
            .then(function (r) { return r && r.ok ? r.json() : null; })
            .then(function (cart) {
              if (!cart) return;
              noteCart(cart);
              var a = (cart.attributes || {});
              // Re-stamp when the cart has lines and is missing our mark, or
              // when it disagrees with what we hold.
              if (cart.item_count > 0 && !cartHasMark(a)) writeCart(true);
              else writeCart(false);
            })
            .catch(function () {});
        } catch (e) {}
      }, REASSERT_MS);
    } catch (e) {}

    /*
      7. DYNAMIC / EXPRESS CHECKOUT BUTTONS.

      This theme renders none today — there is no `{{ form | payment_button }}`
      anywhere, and the live PDP contains no shopify-payment-button element
      (measured, see docs). This handler is defensive: the moment anyone turns
      dynamic checkout on, or a future section adds the tag, the press is
      already covered instead of silently losing attribution.

      pointerdown fires before the button's own click handling starts the
      redirect, which is the only moment we reliably get. keepalive lets the
      request outlive the navigation. We deliberately do NOT await it or
      preventDefault — a purchase must never be delayed or blocked to save an
      attribute.
    */
    try {
      var EXPRESS = [
        'shopify-payment-button',
        '.shopify-payment-button__button',
        '[data-shopify="payment-button"]',
        '.dynamic-checkout__content',
        '.shopify-cleanslate',
        '[data-testid="Checkout-button"]'
      ].join(',');
      var expressWrite = function (e) {
        try {
          var t = e.target;
          if (!t || !t.closest) return;
          if (t.closest(EXPRESS)) {
            writeCart(true, true);
            try { captureCheckoutStarted(); } catch (err) {}
          }
        } catch (err) {}
      };
      document.addEventListener('pointerdown', expressWrite, true);
      document.addEventListener('click', expressWrite, true);
    } catch (e) {}
  });

  /* ═══════════════════════════════════════════ PostHog funnel surfacing ══
     Everything below is additive and optional. PostHog may be switched off in
     theme settings, in which case window.posthog never exists and every
     function here returns without doing anything. Nothing in this block may
     throw into the page — the attribution capture above must keep working
     even if analytics is broken.

     Why the events are explicit rather than autocaptured: autocapture keys off
     DOM shape, so a button rename silently breaks the funnel. These four
     events are the funnel we actually report on, so they are emitted by hand
     with the money on them.
  */

  /* Every key here becomes a PostHog super property and rides on every event,
     so a funnel can be cut by any of them without extra wiring.

     placement and network were already captured by touchFrom() and persisted
     first-touch, but were never registered — so the only way to cut by
     placement was to parse $current_url, which fails for every session whose
     landing pageview did not record (7 of 64 paid sessions in the last read)
     and for every event after the visitor navigates away from the tagged URL.
     Registering them makes placement a first-class dimension: it survives the
     landing pageview being missed, and it survives an organic revisit,
     because state.ft is written once and never again. */
  var FT_PROP_KEYS = [
    ['mije_ft_ad_id', 'ad_id'],
    ['mije_ft_adset_id', 'adset_id'],
    ['mije_ft_campaign_id', 'campaign_id'],
    ['mije_ft_source', 'source'],
    ['mije_ft_medium', 'medium'],
    ['mije_ft_campaign', 'campaign'],
    ['mije_ft_placement', 'placement'],
    ['mije_ft_network', 'network']
  ];

  function ftProps() {
    var ft = (state && state.ft) || {};
    var out = {};
    for (var i = 0; i < FT_PROP_KEYS.length; i++) {
      out[FT_PROP_KEYS[i][0]] = ft[FT_PROP_KEYS[i][1]] || null;
    }
    return out;
  }

  function ph() {
    try {
      return (window.posthog && typeof window.posthog.capture === 'function') ? window.posthog : null;
    } catch (e) { return null; }
  }

  /* posthog.init() runs in mije-analytics.liquid, which is rendered after this
     script tag. The official snippet installs a queueing stub synchronously,
     so calls made before the library finishes loading are replayed — but only
     once the stub exists. Poll briefly rather than assume an order. */
  function whenPostHog(fn) {
    var tries = 0;
    (function attempt() {
      if (ph()) { try { fn(ph()); } catch (e) {} return; }
      if (tries++ > 40) return;              // ~10s, then give up quietly
      window.setTimeout(attempt, 250);
    })();
  }

  var registered = false;
  function registerSuperProps() {
    whenPostHog(function (p) {
      if (registered) return;
      registered = true;
      // Super properties ride on EVERY event from here on, including
      // autocaptured ones and $pageview — that is the point: any funnel or
      // trend in PostHog can be broken down by ad without extra wiring.
      try { p.register(ftProps()); } catch (e) {}
    });
  }

  /*
    Re-register after the cart has told us who this visitor is.

    registerSuperProps() runs once, synchronously, off whatever storage held at
    load. When storage held nothing the props register as nulls — which is
    exactly what order #1657 shows in PostHog: the ad id present on the landing
    pageview and null on every event after it. reconcileWithCart() can recover
    the record a moment later, and this pushes the corrected values over the
    nulls so the rest of the session is attributable again.

    register() overwrites by key, so this is idempotent.
  */
  function reregisterSuperProps() {
    whenPostHog(function (p) {
      try { p.register(ftProps()); } catch (e) {}
    });
  }

  function phCapture(event, props) {
    whenPostHog(function (p) {
      var payload = ftProps();
      try {
        if (props) Object.keys(props).forEach(function (k) {
          if (props[k] !== undefined && props[k] !== null) payload[k] = props[k];
        });
      } catch (e) {}
      try { p.capture(event, payload); } catch (e) {}
    });
  }

  function ctx() {
    try { return window.__mijeAnalytics || {}; } catch (e) { return {}; }
  }

  /* __mijeAnalytics is only rendered when at least one vendor key is set in
     theme settings, so it cannot be the sole source of page context — the
     funnel would then depend on which analytics the merchant happens to have
     configured. Shopify's own ShopifyAnalytics.meta is always present and is
     used as the fallback. */
  /* Copy `value` onto `obj[key]` only when it is a real value. Absent beats
     null: a null product_type in PostHog looks like a product with no
     category, which is a different claim from "we could not read it here". */
  function put(obj, key, value) {
    if (value === undefined || value === null || value === '') return obj;
    obj[key] = value;
    return obj;
  }

  /* Shopify's own meta is the only place product_type is exposed to theme JS
     on a PDP, so it is read even when __mijeAnalytics is present — that
     context comes from Liquid and is settings-gated, meta is always there. */
  function metaProduct() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (!m || !m.page || m.page.pageType !== 'product' || !m.product) return null;
      return m.product;
    } catch (e) { return null; }
  }

  function productContext() {
    var c = ctx();
    var mp = metaProduct();
    var out = null;

    if (c.product && c.product.id) {
      out = {};
      put(out, 'id', c.product.id);
      put(out, 'handle', c.product.handle);
      put(out, 'variant_id', c.product.variant_id);
      put(out, 'price', c.product.price);
      put(out, 'title', c.product.title);
      put(out, 'type', c.product.type);        // added to the Liquid context
    } else if (mp) {
      var v0 = (mp.variants || [])[0] || {};
      out = {};
      put(out, 'id', mp.id);
      put(out, 'handle', mp.handle);
      put(out, 'variant_id', v0.id);
      put(out, 'price', typeof v0.price === 'number' ? v0.price / 100 : null);
      // meta carries no product title — only variants[].name, which is
      // "Product - Variant" and would have to be split on a separator that is
      // not guaranteed. Omitted rather than guessed.
    }

    if (!out) return null;

    // type: Liquid first, Shopify's meta as the always-present fallback.
    if (out.type === undefined && mp) put(out, 'type', mp.type);

    // variant_title, matched to the variant actually selected rather than
    // assuming the first one.
    if (mp) {
      try {
        var vars = mp.variants || [];
        var match = null;
        for (var i = 0; i < vars.length; i++) {
          if (String(vars[i].id) === String(out.variant_id)) { match = vars[i]; break; }
        }
        if (!match && vars.length === 1) match = vars[0];
        if (match) put(out, 'variant_title', match.public_title);
      } catch (e) {}
    }
    return out;
  }

  function currency() {
    var c = ctx().currency;
    return c || 'SGD';
  }

  /* Cart total, kept fresh for free: /cart/update.js answers with the whole
     cart, so the attribution write we already make doubles as a price feed.
     Avoids a speculative /cart.js at checkout-click time, which would race the
     navigation. */
  var cartTotal = null;
  var cartItems = null;
  function noteCart(cart) {
    try {
      if (!cart || typeof cart.total_price !== 'number') return;
      cartTotal = cart.total_price / 100;
      cartItems = cart.item_count;
    } catch (e) {}
  }

  registerSuperProps();

  // ---- mije_product_view -------------------------------------------------
  try {
    var pc = productContext();
    if (pc) {
      var pvProps = { currency: currency() };
      put(pvProps, 'product_id', pc.id);
      put(pvProps, 'product_handle', pc.handle);
      put(pvProps, 'variant_id', pc.variant_id);
      put(pvProps, 'product_type', pc.type);
      put(pvProps, 'product_title', pc.title);
      put(pvProps, 'variant_title', pc.variant_title);
      put(pvProps, 'price', pc.price);
      // `value` is the monetary value of the event and predates `price`;
      // for a view the two are the same number. Kept so existing insights
      // built on `value` do not go blank.
      put(pvProps, 'value', pc.price);
      phCapture('mije_product_view', pvProps);
    }
  } catch (e) {}

  // ---- mije_add_to_cart --------------------------------------------------
  /* Fired from the same interception points that trigger the cart write, so
     it cannot drift from the attribution stamp. `line` is the added line as
     Shopify echoes it back. */
  /* One add can reach us three ways — the theme's own event, our fetch wrapper
     and our XHR wrapper — and PostHog would count each of them. Collapse them:
     the same variant added inside a short window is one add-to-cart. */
  var lastAdd = { key: '', at: 0 };
  var ADD_DEDUPE_MS = 4000;

  /* /cart/add.js answers in TWO shapes depending on how it was called:
     posting {items:[…]} echoes {items:[line, …]}, while a single form-encoded
     add echoes the bare line. The theme posts the items form, so what arrived
     here was the WRAPPER — every read of line.product_id was undefined and
     mije_add_to_cart shipped with nothing but currency on it. Normalise
     first, then read. */
  function addedLines(payload) {
    try {
      if (!payload || typeof payload !== 'object') return [];
      if (Array.isArray(payload.items)) return payload.items.filter(Boolean);
      if (Array.isArray(payload)) return payload.filter(Boolean);
      if (payload.id || payload.variant_id) return [payload];
    } catch (e) {}
    return [];
  }

  function captureAddToCart(payload) {
    var lines = addedLines(payload);
    var line = lines[0] || null;

    var key = '';
    try { key = String((line && (line.variant_id || line.id)) || 'unknown'); } catch (e) { key = 'unknown'; }
    var now = Date.now();
    // An unidentified add (no body parsed) must not silently swallow a real
    // one for a different variant, so the key participates in the check.
    if (lastAdd.key && (key === lastAdd.key || key === 'unknown' || lastAdd.key === 'unknown') &&
        (now - lastAdd.at) < ADD_DEDUPE_MS) {
      return;
    }
    lastAdd = { key: key, at: now };

    var props = { currency: currency() };
    try {
      if (line) {
        put(props, 'product_id', line.product_id);
        put(props, 'variant_id', line.variant_id || line.id);
        put(props, 'product_handle', line.handle);
        put(props, 'product_type', line.product_type);
        put(props, 'product_title', line.product_title || line.untranslated_product_title);
        put(props, 'variant_title', line.variant_title || line.untranslated_variant_title);
        put(props, 'quantity', line.quantity);

        // unit price, matching mije_product_view's `price`
        if (typeof line.final_price === 'number') put(props, 'price', line.final_price / 100);
        else if (typeof line.price === 'number') put(props, 'price', line.price / 100);

        // `value` stays the LINE total (price x quantity) — it is the
        // monetary value of the event, and existing insights read it.
        if (typeof line.final_line_price === 'number') put(props, 'value', line.final_line_price / 100);
        else if (typeof line.line_price === 'number') put(props, 'value', line.line_price / 100);
        else if (typeof line.price === 'number') put(props, 'value', line.price / 100);

        /* A bundle adds several lines in one action. That is still ONE
           add-to-cart by the shopper, so it stays one event — firing per line
           would inflate the funnel step against every count already recorded.
           These two make the rest of the add visible instead of silently
           reporting it as the first line alone. */
        if (lines.length > 1) {
          put(props, 'line_count', lines.length);
          var types = [];
          for (var i = 0; i < lines.length; i++) {
            var t = lines[i] && lines[i].product_type;
            if (t && types.indexOf(t) === -1) types.push(t);
          }
          if (types.length) put(props, 'product_types', types);
        }
      }
    } catch (e) {}
    phCapture('mije_add_to_cart', props);
  }

  // ---- mije_checkout_started ---------------------------------------------
  var checkoutFired = false;
  function captureCheckoutStarted() {
    if (checkoutFired) return;               // one per page — double-submit is not two checkouts
    checkoutFired = true;
    phCapture('mije_checkout_started', {
      value: cartTotal,
      currency: currency(),
      item_count: cartItems
    });
  }

  // ---- mije_purchase -----------------------------------------------------
  /* Theme JS does not run on Shopify's checkout, so this only fires where the
     order object is actually available: the order-status page, when the
     merchant has pasted the loader into Checkout → Additional scripts (see
     docs/analytics-setup.md). Left in place so that the day it is pasted, the
     event carries the same ft_* properties as the rest of the funnel; the
     server-side truth for purchases stays the order tags written by
     analytics/attribution/tag_orders.py. */
  try {
    var co = window.Shopify && window.Shopify.checkout;
    if (co && co.order_id) {
      phCapture('mije_purchase', {
        order_id: co.order_id,
        value: Number(co.total_price) || null,
        currency: co.currency || currency()
      });
    }
  } catch (e) {}

  // Expose the hooks the capture paths above call into.
  window.__mijeFunnel = {
    addToCart: captureAddToCart,
    checkoutStarted: captureCheckoutStarted,
    noteCart: noteCart,
    props: ftProps
  };

  // Small read-only surface for verification and for the docs' debug recipe.
  try {
    window.MijeAttribution = {
      version: VERSION,
      state: function () { return state; },
      attributes: buildAttributes,
      flush: function () { writeCart(true); },
      ftProps: ftProps
    };
  } catch (e) {}
})();
